import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { Button, Dialog, Header3 } from "@activecollab/components";
import { Form, Formik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { showErrorToast } from "~/components/Toast";
import { useAddApiToken } from "~/repository";
import ClientFormCheckbox from "../components/ClientFormCheckbox";
import ClientFormInput from "../components/ClientFormInput";

const ApiTokenAddDialog = ({ open, onClose, userId, setSelectedTokenId }) => {
  const { t } = useTranslation();
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();
  const addTokenMutation = useAddApiToken();
  const client = useQueryClient();

  const handleAddToken = useCallback(
    ({ is_read_only, ...rest }) => {
      const data = {
        ...rest,
        is_read_write: !is_read_only,
      };

      addTokenMutation.mutate(
        { userId, data },
        {
          onSuccess: (result) => {
            setSelectedTokenId(result.single.id);
            onClose();
            client.invalidateQueries(["api-tokens"]);
          },
          onError: () => {
            showErrorToast(t("Unable to add token"));
          },
        }
      );
    },
    [addTokenMutation, client, onClose, setSelectedTokenId, t, userId]
  );

  const validationSchema = yup.object().shape({
    password: yup.string().trim().required(t("Password is required")),
    client_name: yup.string().trim().required(t("Client name is required")),
    client_vendor: yup.string().trim().required(t("Vendor name is required")),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="tw-max-h-[calc(100vh-175px)] tw-flex tw-flex-col"
    >
      <Formik
        initialValues={{ password: "", client_name: "", client_vendor: "", is_read_only: true }}
        onSubmit={handleAddToken}
        validateOnMount
        validationSchema={validationSchema}
      >
        {({ errors, handleSubmit }) => {
          const handleFormSubmit = (e) => {
            e.preventDefault();
            handleSubmit();
            if (!Object.keys(errors)?.length) return;
            focusFirstInvalidField(errors, false);
          };
          return (
            <Form
              onSubmit={handleFormSubmit}
              className="tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden"
            >
              <Dialog.Title disableDefaultHeading>
                <Header3>{t("Add Token")}</Header3>
              </Dialog.Title>
              <Dialog.ContentDivider />
              <Dialog.Content className="tw-overflow-y-auto tw-flex-1">
                <div className="tw-flex tw-flex-col tw-gap-4">
                  <ClientFormInput
                    type="password"
                    name="password"
                    label={`${t("Your Password")}*`}
                    caption={t("We need to make sure it’s you")}
                    fieldRef={fieldRef}
                  />
                  <ClientFormInput
                    name="client_name"
                    label={`${t("Client")}*`}
                    caption={t("Name of the app that will use this token")}
                    fieldRef={fieldRef}
                  />
                  <ClientFormInput
                    name="client_vendor"
                    label={`${t("Vendor")}*`}
                    caption={t("Name of the organization that made the app")}
                    fieldRef={fieldRef}
                  />
                  <ClientFormCheckbox
                    name="is_read_only"
                    label={t("Token is read-only")}
                  />
                </div>
              </Dialog.Content>
              <Dialog.ContentDivider />
              <Dialog.Actions>
                <Button
                  className="tw-mr-3"
                  disabled={addTokenMutation.isLoading}
                  type="submit"
                >
                  {t("Add Token")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={addTokenMutation.isLoading}
                >
                  {t("Cancel")}
                </Button>
              </Dialog.Actions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

ApiTokenAddDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  userId: PropTypes.number,
  setSelectedTokenId: PropTypes.func,
};

export default ApiTokenAddDialog;
